import { useCallback, useEffect, useRef, useState } from 'react'
import {
  type AgentEvent,
  EMPTY_RUN_ACTIVITY,
  type RunActivity,
  reduceAgentEvent,
} from '@/frontend/features/AgentChats/events/agentEvents'

interface UseAgentChatEventsOptions {
  /** Resets the accumulator when the open conversation changes. */
  sessionId: string | null
  /** The turn currently in flight, or null when the thread is idle. */
  pendingTurnId: string | null
  /** Monotonic retry number for a durable turn reused across attempts. */
  pendingTurnAttempt: number
  /** True only while a newly submitted turn may not have reached session state yet. */
  acceptEventsWithoutPendingTurn: boolean
}

// Accumulates the run event stream into a RunActivity, separately from
// useAgentChatController.
//
// Deliberately its own hook: the controller is already ~1000 lines of send and
// reconnect bookkeeping, and event folding has a different lifecycle (it is
// per-run and disposable) than session state (which is per-conversation and
// authoritative).
//
// Ordering and duplicates are handled here rather than trusted from the wire:
// Action Cable can redeliver after a reconnect, and the Gateway's `seq` is
// per-run, so the same number reappears on the next turn.
export function useAgentChatEvents({
  sessionId,
  pendingTurnId,
  pendingTurnAttempt,
  acceptEventsWithoutPendingTurn,
}: UseAgentChatEventsOptions) {
  const [activity, setActivity] = useState<RunActivity | null>(null)
  const seenRef = useRef<Set<string>>(new Set())
  const scopeRef = useRef<string | null>(null)
  const sessionRef = useRef<string | null>(sessionId)
  const ignoredTurnRef = useRef<string | null>(null)

  const resetScope = useCallback((nextScope: string | null) => {
    scopeRef.current = nextScope
    seenRef.current = new Set()
    setActivity(null)
  }, [])

  // A new conversation or a new turn starts a fresh accumulator. When the
  // pending turn becomes null, however, keep the completed stream until its
  // durable assistant message arrives. Clearing it on the terminal summary
  // creates a visible blank frame between the last delta and message_created.
  useEffect(() => {
    if (sessionRef.current !== sessionId) {
      sessionRef.current = sessionId
      ignoredTurnRef.current = null
      const nextScope = sessionId && pendingTurnId
        ? `${sessionId}:${pendingTurnId}:${pendingTurnAttempt}`
        : null
      resetScope(nextScope)
      return
    }

    if (!sessionId) {
      if (scopeRef.current !== null) resetScope(null)
      return
    }

    if (!pendingTurnId) return

    if (ignoredTurnRef.current !== pendingTurnId) ignoredTurnRef.current = null
    const nextScope = sessionId && pendingTurnId
      ? `${sessionId}:${pendingTurnId}:${pendingTurnAttempt}`
      : null
    if (scopeRef.current !== nextScope) resetScope(nextScope)
  }, [sessionId, pendingTurnAttempt, pendingTurnId, resetScope])

  const applyEvent = useCallback((incomingSessionId: string, event: AgentEvent) => {
    if (incomingSessionId !== sessionId) return
    if (ignoredTurnRef.current === event.turn_id) return
    // The first event can beat the authoritative session summary for a newly
    // submitted turn. Outside that narrow window, a missing pending turn means
    // the conversation is idle and delayed frames from the old run must not
    // resurrect its Activity panel.
    if (!pendingTurnId && !acceptEventsWithoutPendingTurn) return
    if (pendingTurnId && event.turn_id !== pendingTurnId) return

    const scope = `${incomingSessionId}:${event.turn_id}:${event.attempt ?? pendingTurnAttempt}`
    if (scopeRef.current !== scope) {
      // An event for a turn we have not scoped to yet (the summary that would
      // have told us is still in flight). Adopt it rather than drop the run.
      scopeRef.current = scope
      seenRef.current = new Set()
      setActivity(null)
    }

    // Dedup key is scoped to the turn because `seq` restarts per run, and falls
    // back to the row id for events the Gateway did not number.
    const dedupeKey = event.seq === null
      ? `id:${event.id ?? ''}`
      : `${event.turn_id}:${event.seq}:${event.event_type}`
    if (dedupeKey !== 'id:' && seenRef.current.has(dedupeKey)) return
    seenRef.current.add(dedupeKey)

    setActivity((current) =>
      reduceAgentEvent(current ?? { ...EMPTY_RUN_ACTIVITY, turnId: event.turn_id }, event),
    )
  }, [acceptEventsWithoutPendingTurn, pendingTurnAttempt, pendingTurnId, sessionId])

  /** Replaces the accumulator from a replay response after a reconnect. */
  const replaceFromReplay = useCallback((incomingSessionId: string, events: AgentEvent[]) => {
    if (incomingSessionId !== sessionId || events.length === 0) return
    if (!pendingTurnId && !acceptEventsWithoutPendingTurn) return

    const scopedEvents = pendingTurnId
      ? events.filter((event) => event.turn_id === pendingTurnId)
      : events
    if (scopedEvents.length === 0) return

    const turnId = scopedEvents[scopedEvents.length - 1].turn_id
    if (ignoredTurnRef.current === turnId) return
    const replayAttempt = scopedEvents[scopedEvents.length - 1].attempt ?? pendingTurnAttempt
    const nextScope = `${incomingSessionId}:${turnId}:${replayAttempt}`
    const sameScope = scopeRef.current === nextScope
    const previouslySeen = seenRef.current
    scopeRef.current = nextScope
    const seen = new Set<string>()
    const next = scopedEvents.reduce<RunActivity>((state, event) => {
      const key = event.seq === null
        ? `id:${event.id ?? ''}`
        : `${event.turn_id}:${event.seq}:${event.event_type}`
      if (key !== 'id:' && seen.has(key)) return state
      seen.add(key)
      return reduceAgentEvent(state, event)
    }, { ...EMPTY_RUN_ACTIVITY, turnId })
    seenRef.current = sameScope
      ? new Set([ ...previouslySeen, ...seen ])
      : seen
    setActivity((current) => {
      if (!sameScope || current?.turnId !== turnId) return next

      // Text deltas are intentionally transient and therefore absent from the
      // durable replay. A periodic session load for the same turn must enrich
      // tools/plan without erasing text already received over Action Cable.
      return {
        ...next,
        text: current.text || next.text,
        textSegments: current.textSegments.length > 0 ? current.textSegments : next.textSegments,
        startedAt: current.startedAt || next.startedAt,
      }
    })
  }, [acceptEventsWithoutPendingTurn, pendingTurnAttempt, pendingTurnId, sessionId])

  const clearActivity = useCallback(() => {
    ignoredTurnRef.current = activity?.turnId ?? pendingTurnId
    resetScope(null)
  }, [activity?.turnId, pendingTurnId, resetScope])

  // A retry reuses the durable turn id. Clear events from the failed attempt,
  // but keep that id eligible so the replacement run can stream immediately.
  const restartActivity = useCallback(() => {
    ignoredTurnRef.current = null
    resetScope(null)
  }, [resetScope])

  return { activity, applyEvent, replaceFromReplay, clearActivity, restartActivity }
}
